import { ReactElement, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { Input, PrimaryButton, SecondaryButton, SnackbarStatus, StatusBar } from '@weareopenr/component-library';
import { updatePriceScheme } from 'src/api/updatePriceScheme';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { usePriceSchemes } from 'src/hooks/usePriceSchemes';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import { PublicationStatus } from 'src/types/price.types';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import { IEditPriceSchemeNameFormValues } from './types';
import styles from './EditPriceSchemeNameTrayContent.module.scss';

export const DUPLICATE_NAME_ERROR = 'Price scheme already exists';
export const LIVE_SCHEME_WARNING = 'This scheme is live. The new name will reach channels on the next publish.';

const LIVE_STATUSES = [PublicationStatus.IN_USE, PublicationStatus.SCHEDULED];

export interface EditPriceSchemeNameTrayContentProps {
  priceSchemeId: string;
  currentName: string;
  /** Null for schemes still being created, which have no publication status yet. */
  publicationStatus: PublicationStatus | null;
  onClose: () => void;
  openId: number;
}

export const EditPriceSchemeNameTrayContent = ({
  priceSchemeId,
  currentName,
  publicationStatus,
  onClose,
  openId,
}: EditPriceSchemeNameTrayContentProps): ReactElement => {
  const queryClient = useQueryClient();
  const { trackEvent, captureException } = useUserTracking();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { data: priceSchemes = [] } = usePriceSchemes();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = useForm<IEditPriceSchemeNameFormValues>({
    mode: 'onChange',
    defaultValues: { name: currentName },
  });

  useEffect(() => {
    reset({ name: currentName });
    clearError();
  }, [openId]);

  // Names created before the API trimmed them can carry surrounding whitespace, and the
  // rename trims, so compare both sides trimmed or Save would enable on an untouched form.
  const isUnchanged = watch('name').trim() === currentName.trim();

  const onSubmit = async ({ name }: IEditPriceSchemeNameFormValues) => {
    clearError();

    try {
      await updatePriceScheme(priceSchemeId, { name: name.trim() });

      await queryClient.invalidateQueries([QueryKeys.priceSchemes, getBrandId()]);
      trackEvent('price_scheme_renamed', { priceSchemeId });
      showNotification({ status: SnackbarStatus.SUCCESS, message: 'Price scheme name updated' });
      onClose();
    } catch (error) {
      captureException(error, { tags: { action: 'price_scheme_rename' } });
      const message = error instanceof Error ? error.message : undefined;
      showNotification({
        status: SnackbarStatus.ERROR,
        message: message || 'Failed to rename price scheme. Please try again.',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles['content']}>
      <div>
        {LIVE_STATUSES.includes(publicationStatus) && (
          <StatusBar className={styles['warning']} message={LIVE_SCHEME_WARNING} variant="warning" />
        )}

        <Input
          type="text"
          label="Price scheme name"
          register={register('name', {
            required: 'Please enter a price scheme name',
            maxLength: { value: 255, message: 'Name must be 255 characters or fewer' },
            validate: {
              notEmpty: (value) => value.trim().length > 0 || 'Please enter a price scheme name',
              isUnique: (value) => {
                const candidate = value.trim().toLocaleLowerCase();
                const isDuplicate = priceSchemes.some(
                  ({ id, name }) => id !== priceSchemeId && name.toLocaleLowerCase() === candidate,
                );
                return !isDuplicate || DUPLICATE_NAME_ERROR;
              },
            },
          })}
          error={errors?.name?.message}
        />
      </div>

      {errorMessage && <StatusBar message={errorMessage} variant="error" />}

      <div className={styles['button-row']}>
        <ButtonContainer
          direction="column-reverse"
          margin="none"
          left={
            <SecondaryButton buttonStyle="outline" type="button" size="full-width" onClick={onClose}>
              Cancel
            </SecondaryButton>
          }
          right={
            <PrimaryButton
              type="submit"
              size="full-width"
              loading={isSubmitting}
              disabled={isSubmitting || !isValid || isUnchanged}
            >
              Save
            </PrimaryButton>
          }
        />
      </div>
    </form>
  );
};

export default EditPriceSchemeNameTrayContent;
